import { cn } from "@/lib/utils";

export interface HeroKpi {
  key: string;
  value: number;
  label: string;
  /** Leads the row in a warm tone while non-zero (overdue work). */
  urgent?: boolean;
  onClick?: () => void;
}

interface DashboardHeroProps {
  greeting: string;
  subtitle: string;
  kpis: HeroKpi[];
}

/** The colour moment of the dashboard, after the owner's nidria front (2026-09-29):
 *  an indigo band with a cast shadow, the greeting, the date and what waits, and the
 *  key numbers in white, each clickable. A zero number recedes. */
export function DashboardHero({ greeting, subtitle, kpis }: DashboardHeroProps) {
  return (
    <section
      className="relative overflow-hidden rounded-[22px] p-6 text-white sm:p-8"
      style={{
        background: "linear-gradient(115deg, #1e1b4b 0%, #312e81 55%, #4338ca 100%)",
        boxShadow: "0 1px 2px rgba(79, 70, 229, 0.12), 0 16px 40px rgba(79, 70, 229, 0.28)",
      }}
    >
      <svg
        aria-hidden
        viewBox="0 0 400 400"
        className="pointer-events-none absolute -right-16 -top-24 h-[360px] w-[360px] opacity-[0.08]"
        fill="none"
      >
        <circle cx="200" cy="200" r="120" stroke="white" strokeWidth="1.5" />
        <circle cx="230" cy="170" r="170" stroke="white" strokeWidth="1.5" />
        <circle cx="180" cy="230" r="70" stroke="white" strokeWidth="1.5" />
      </svg>

      <div className="relative">
        <h1 className="text-[30px] font-medium leading-tight tracking-tight">{greeting}</h1>
        <p className="mt-1.5 text-sm text-white/70">{subtitle}</p>

        <div className="mt-6 flex flex-wrap items-start gap-x-6 gap-y-2 sm:gap-x-10">
          {kpis.map((kpi) => (
            <button
              key={kpi.key}
              type="button"
              onClick={kpi.onClick}
              className={cn(
                "-ml-3 rounded-xl px-3 py-2 text-left transition-colors duration-150 hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50",
                kpi.value === 0 && "opacity-40",
              )}
            >
              <span
                className={cn(
                  "block text-[32px] font-medium leading-none tabular-nums",
                  kpi.urgent && kpi.value > 0 ? "text-amber-200" : "text-white",
                )}
              >
                {kpi.value}
              </span>
              <span className="mt-1.5 block text-xs text-white/70">{kpi.label}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
